// frontend/src/pages/public/FeaturesPage.jsx
import React from "react";
import {
  ArrowPathIcon,
  ChartBarIcon,
  MapIcon,
  BeakerIcon,
  CodeBracketIcon,
  FolderIcon,
  ArrowDownTrayIcon,
  UserGroupIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import Seo from "../../marketing/Seo";
import { Container, Section, Eyebrow, Button } from "../../marketing/ui";
import { FeatureGrid, CTASection } from "../../marketing/blocks";

const CORE = [
  { icon: ArrowPathIcon, title: "Dynamic codes & redirects", description: "Change a code's destination any time without reprinting — on every plan, including Free." },
  { icon: ChartBarIcon, title: "Real-time analytics", description: "Watch scans land live, broken down by device, browser, and UTM campaign." },
  { icon: MapIcon, title: "Location heatmaps", description: "See where your audience scans from, at a glance (Pro and above)." },
  { icon: BeakerIcon, title: "A/B testing", description: "Split traffic across weighted variants and keep what converts (Pro and above)." },
  { icon: CodeBracketIcon, title: "Developer API", description: "Automate codes and pull analytics with scoped API keys and webhooks (Pro and above)." },
  { icon: FolderIcon, title: "Folders & tags", description: "Organise thousands of codes and filter by campaign, location, or team." },
  { icon: ArrowDownTrayIcon, title: "CSV import & export", description: "Bulk-create codes and export your data whenever you need it." },
  { icon: UserGroupIcon, title: "Teams & roles", description: "Invite members into an organisation with role-based access." },
  { icon: EyeSlashIcon, title: "Privacy-first analytics", description: "Insightful analytics that never store a raw IP address — GDPR by design." },
];

export default function FeaturesPage() {
  return (
    <>
      <Seo
        title="Features"
        description="Dynamic QR codes, real-time analytics, A/B testing, a developer API, heatmaps, teams, and privacy-first tracking."
        path="/features"
      />

      <div className="bg-gradient-to-b from-brand-50/60 to-white">
        <Container className="py-16 text-center sm:py-20">
          <Eyebrow>Features</Eyebrow>
          <h1 className="mx-auto mt-2 max-w-3xl text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            A focused toolkit for dynamic QR
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-600">
            Everything you need to create, organise, automate, and measure QR codes — and
            nothing you don't.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button to="/register" size="lg">Get started free</Button>
            <Button to="/pricing" size="lg" variant="secondary">Compare plans</Button>
          </div>
        </Container>
      </div>

      <FeatureGrid
        title="Built for builders and marketers alike"
        subtitle="Some advanced features unlock on Pro and Business — see the pricing page for details."
        features={CORE}
      />

      <CTASection
        title="See it in action"
        subtitle="Create five dynamic codes free and explore the full dashboard."
        primary={{ to: "/register", label: "Get started free" }}
        secondary={{ to: "/solutions", label: "Explore use-cases" }}
      />
    </>
  );
}
